import React from 'react'
import { observer } from 'mobx-react-lite'

import { ListItem, ListItemText } from '@material-ui/core'

import { DropdownButton } from './DropdownButton'

import { useLogic } from '../hooks/storeHook'
import { useBoolState } from '../hooks/commonHooks'
import { useDefaultMenuItemStyles } from '../hooks/defaultSelectStylesHook'

import { buildExcelExportFilename } from '../common/stringUtility'

import { TableColumn } from '../types/tableTypes'
import { WbProductColumnId } from '../types/wbColumns'
import { WbOrderColumnId } from '../types/wbOrderColumns'
import { OzonProductColumnId } from '../types/ozonColumns'
import { selectColumnId } from '../types/myProductColumnIds'
import { OzonOrderColumnId } from '../types/ozonOrderColumns'
import { YandexMarketProductColumnId } from '../types/yandexMarketColumns'
import { YandexMarketOrderColumnId } from '../types/yandexMarketOrderColumns'

export enum ExportFileKind {
  Products = 1,

  Orders = 2
}

export type ExportToExcelButtonProps<TColumnId extends string, TRow extends object> = {
  fileKind: ExportFileKind
  columns: Array<TableColumn<TColumnId, TRow>>
  accountName: string | undefined
  makeExportRequest: (columnIds: TColumnId[]) => Promise<Blob>
}

type CommonColumnId =
  (OzonProductColumnId & WbProductColumnId & YandexMarketProductColumnId)
  | (OzonOrderColumnId & WbOrderColumnId & YandexMarketOrderColumnId)

const excludedColumnIds: readonly string[] =
  [selectColumnId, 'logs', 'syncErrorId'] as const satisfies readonly CommonColumnId[]

export const ExportToExcelButton =
  observer(
    <TColumnId extends string, TRow extends object>(
      {
        columns,
        accountName,
        makeExportRequest,
        fileKind
      }: ExportToExcelButtonProps<TColumnId, TRow>) => {
      const api = useLogic().api
      const menuClasses = useDefaultMenuItemStyles()

      const [isExporting, setExporting, setExported] = useBoolState()

      const onExport =
        async (setClosed: VoidFunction) => {
          setClosed()

          if (isExporting) {
            return
          }

          setExporting()

          try {

            const exportColumnIds = columns.map(c => c.id)
                                           .filter(id => !excludedColumnIds.includes(id))

            const exportedBlob = await makeExportRequest(exportColumnIds)

            api.downloadBlob(exportedBlob, buildExcelExportFilename(accountName, fileKind))
          } finally {
            setExported()
          }
        }

      return (
        <DropdownButton text={isExporting ? 'Экспортируем...' : 'Экспорт'} popupWidth={120} disabled={isExporting}>
          {
            buttonProps =>
              <ListItem
                className={menuClasses.menuItem}
                button
                onClick={() => onExport(buttonProps.setClosed)}
              >
                <ListItemText primary="В файл .xlsx"/>
              </ListItem>
          }
        </DropdownButton>
      )
    })